import React from 'react';
import { Video, ExternalLink, Download, Calendar, Clock } from 'lucide-react';

const CurriculumConference = () => {
  const schedule = [
    { group: 'Grade K–4 Teachers', note: 'Each grade presents separately', cycle1: false, cycle2: false, cycle3: false, kFour: true },
    { group: 'Grade 5 (For current 4th grade families)', cycle1: false, cycle2: true, cycle3: false },
    { group: 'Grade 6 (For current 5th grade families)', cycle1: false, cycle2: false, cycle3: true },
    { group: 'Grade 7 (For current 6th grade families)', cycle1: true, cycle2: false, cycle3: false },
    { group: 'Grade 8 (For current 7th grade families)', cycle1: false, cycle2: true, cycle3: false },
    { group: 'End of Year Meeting — Current 8th Grade Families', cycle1: false, cycle2: true, cycle3: false },
    { group: 'Cluster Teachers (Grades K–8): Phys. Ed, Art, Technology, Music, Chorus', cycle1: false, cycle2: false, cycle3: true },
    { group: 'Cluster Teachers (Grades 5–8): Spanish, Civics & Social Justice, Inquiry & Explorations, Instrumental Music', cycle1: false, cycle2: false, cycle3: false, note: 'See Zoom link below' },
  ];

  const zoomLinks = [
    {
      grade: 'Kindergarten',
      note: 'For current Pre-K families',
      link: 'https://zoom.us/j/93557957571?pwd=RRzUsR1V5DTYiSDsXqhIH28XGwDkpj.1',
      id: '935 5795 7571',
      passcode: '929414',
    },
    {
      grade: 'First Grade',
      note: 'For current K families',
      link: 'https://zoom.us/j/96292307732?pwd=Fj02JRJvHiznbHJuYhDGK9ZjDwbHGj.1',
      id: '962 9230 7732',
      passcode: '857386',
    },
    {
      grade: 'Second Grade',
      note: 'For current 1st grade families',
      link: 'https://zoom.us/j/95735588383?pwd=POjdylQ8KwBf7QnzFQtqn6eu8tcu9J.1',
      id: '957 3558 8383',
      passcode: '122456',
    },
    {
      grade: 'Third Grade',
      note: 'For current 2nd grade families',
      link: 'https://zoom.us/j/99520306345?pwd=AoasFpe5KBGVUhjOM6WRV2nt2uJ2WS.1',
      id: '995 2030 6345',
      passcode: '398866',
    },
    {
      grade: 'Fourth Grade',
      note: 'For current 3rd grade families',
      link: 'https://zoom.us/j/96865009201?pwd=KgCJ8XWsWHuL5mJHfkdA5JaT6CdsOT.1',
      id: '968 6500 9201',
      passcode: '665600',
    },
    {
      grade: 'Fifth Grade',
      note: 'For current 4th grade families',
      link: 'https://zoom.us/j/96865009201?pwd=KgCJ8XWsWHuL5mJHfkdA5JaT6CdsOT.1',
      id: '968 6500 9201',
      passcode: '665600',
    },
    {
      grade: 'Sixth Grade',
      note: 'For current 5th grade families',
      link: 'https://zoom.us/j/94596071858?pwd=RzoR9uJ1lsinjeMAqPoP1LClEhByDM.1',
      id: '945 9607 1858',
      passcode: '405591',
    },
    {
      grade: 'Seventh Grade',
      note: 'For current 6th grade families',
      link: 'https://zoom.us/j/95864665116?pwd=0AFG15RTp29XQxVZNIftrxWbtGHx4i.1',
      id: '958 6466 5116',
      passcode: '327567',
    },
    {
      grade: 'Eighth Grade',
      note: 'For current 7th grade families',
      link: 'https://zoom.us/j/97411681820?pwd=dus8W4yuqVhcVpobwuLt3Oow3pwrW6.1',
      id: '974 1168 1820',
      passcode: '538689',
    },
    {
      grade: 'Current 8th Grade Families',
      note: 'End of Year & Regents meeting',
      link: 'https://zoom.us/j/97287801485?pwd=UGbxmrGoSbe0KtAyEQFFTrvaiJab4b.1',
      id: '972 8780 1485',
      passcode: '563970',
    },
    {
      grade: 'Cluster Teachers (Grades K–8)',
      note: 'Physical Education, Art, Technology, Music, Chorus',
      link: 'https://zoom.us/j/92661578540?pwd=SHNuT0eqbb9MqTPGWvGdmwQALInDKa.1',
      id: '926 6157 8540',
      passcode: '311804',
    },
    {
      grade: 'Cluster Teachers (Grades 5–8)',
      note: 'Spanish, Civics & Social Justice, Inquiry & Explorations, Instrumental Music',
      link: 'https://zoom.us/j/93334071450?pwd=m8mDXXV8n2DsGHGVYapfZ444B0m4Cn.1',
      id: '933 3407 1450',
      passcode: '337878',
    },
  ];

  const renderZoomLink = (link) => (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center text-primary-600 hover:text-primary-800 font-medium transition-colors duration-200"
    >
      Join Zoom <ExternalLink className="ml-1 h-3.5 w-3.5" />
    </a>
  );

  const checkCell = (active) => (
    <span className={active ? 'text-green-600 font-bold text-lg' : 'text-slate-300'}>
      {active ? '✔' : '—'}
    </span>
  );

  return (
    <main id="main-content" className="bg-white pt-20">
      {/* Hero */}
      <section className="section-padding hero-gradient text-white">
        <div className="container-padding">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-md px-6 py-3 rounded-full border border-white/20 mb-8">
              <Video className="h-5 w-5" />
              <span className="text-sm font-medium tracking-wide uppercase">Virtual Event</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-light mb-8 leading-tight">
              <span className="block font-extralight text-white/90">Curriculum</span>
              <span className="block font-medium">Conferences 2026</span>
            </h1>

            <p className="text-xl text-white/90 mb-8 font-light max-w-3xl mx-auto leading-relaxed">
              Virtual previews for the 2026–2027 school year at Talented &amp; Gifted School
              for Young Scholars (04M012).
            </p>

            <div className="flex flex-wrap justify-center gap-6 mb-8">
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-md px-5 py-3 rounded-full border border-white/20">
                <Calendar className="h-4 w-4" />
                <span className="text-sm font-medium">Thursday, May 14th, 2026</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-md px-5 py-3 rounded-full border border-white/20">
                <Clock className="h-4 w-4" />
                <span className="text-sm font-medium">4:45 pm – 7:45 pm</span>
              </div>
            </div>

            <a
              href="/assets/docs/May_2026_Curriculum_Conferences_Schedule_Letter2026_5-6-26_pm.docx"
              download
              className="inline-flex items-center space-x-2 bg-white text-primary-700 hover:bg-primary-50 border border-primary-200 hover:border-primary-400 px-6 py-3 rounded-lg text-sm font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <Download className="h-4 w-4" />
              <span>Download Letter</span>
            </a>
          </div>
        </div>
      </section>

      {/* About the Event */}
      <section className="section-padding bg-slate-50">
        <div className="container-padding">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-light text-primary-900 mb-2">About This Event</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full mb-8"></div>
            <div className="prose prose-slate max-w-none text-secondary-700 leading-relaxed space-y-4">
              <p>
                We are hosting virtual previews for the <strong>2026–2027 school year</strong> on{' '}
                <strong>Thursday, May 14th, 2026 from 4:45 pm – 7:45 pm on Zoom.</strong> This event
                will provide an opportunity for you to learn about the next grade's curriculum and
                expectations for your child in the 2026–2027 school year.
              </p>
              <p>The previews will occur in three cycles:</p>
              <ul className="list-none space-y-1 pl-0">
                <li className="flex items-center space-x-3">
                  <span className="inline-block w-2 h-2 rounded-full bg-primary-500 flex-shrink-0"></span>
                  <span><strong>Cycle 1:</strong> 4:45 pm – 5:35 pm</span>
                </li>
                <li className="flex items-center space-x-3">
                  <span className="inline-block w-2 h-2 rounded-full bg-primary-500 flex-shrink-0"></span>
                  <span><strong>Cycle 2:</strong> 5:45 pm – 6:35 pm</span>
                </li>
                <li className="flex items-center space-x-3">
                  <span className="inline-block w-2 h-2 rounded-full bg-primary-500 flex-shrink-0"></span>
                  <span><strong>Cycle 3:</strong> 6:45 pm – 7:35 pm</span>
                </li>
              </ul>
              <p>
                Each grade K–4 will present during <strong>two different cycle times.</strong> There will
                be one presentation each for rising 5th, 6th, 7th, and 8th grade. There will also be a
                separate special presentation for current 8th grade families regarding Regents exams
                and end of year activities, beginning at 5:45 pm.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule Table */}
      <section className="section-padding bg-white">
        <div className="container-padding">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-light text-primary-900 mb-2">Presentation Schedule</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full mb-10"></div>

            <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700" tabIndex={0} role="region" aria-label="Presentation schedule table — scroll sideways if it is cut off">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-primary-50 border-b border-primary-100">
                    <th className="px-6 py-4 text-sm font-semibold text-primary-900 uppercase tracking-wider">Teachers / Group</th>
                    <th className="px-6 py-4 text-sm font-semibold text-primary-900 uppercase tracking-wider text-center">
                      Cycle 1<br /><span className="text-xs font-normal text-secondary-600 normal-case">4:45–5:35 pm</span>
                    </th>
                    <th className="px-6 py-4 text-sm font-semibold text-primary-900 uppercase tracking-wider text-center">
                      Cycle 2<br /><span className="text-xs font-normal text-secondary-600 normal-case">5:45–6:35 pm</span>
                    </th>
                    <th className="px-6 py-4 text-sm font-semibold text-primary-900 uppercase tracking-wider text-center">
                      Cycle 3<br /><span className="text-xs font-normal text-secondary-600 normal-case">6:45–7:35 pm</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {schedule.map((row, i) => (
                    <tr key={i} className="hover:bg-primary-50/50 transition-colors duration-150">
                      <td className="px-6 py-4 text-sm font-medium text-primary-900">
                        {row.group}
                        {row.note && (
                          <span className="block text-xs text-secondary-600 font-normal mt-0.5">{row.note}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {row.kFour
                          ? <span className="text-xs text-secondary-600 italic">Multiple cycles</span>
                          : checkCell(row.cycle1)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {row.kFour ? '' : checkCell(row.cycle2)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {row.kFour ? '' : checkCell(row.cycle3)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Zoom Links */}
      <section className="section-padding bg-slate-50">
        <div className="container-padding">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-light text-primary-900 mb-2">Zoom Links</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full mb-10"></div>

            <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700" tabIndex={0} role="region" aria-label="Zoom links table — scroll sideways if it is cut off">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-primary-50 border-b border-primary-100">
                    <th className="px-6 py-4 text-sm font-semibold text-primary-900 uppercase tracking-wider">Grade / Group</th>
                    <th className="px-6 py-4 text-sm font-semibold text-primary-900 uppercase tracking-wider">Zoom Link</th>
                    <th className="px-6 py-4 text-sm font-semibold text-primary-900 uppercase tracking-wider">Meeting ID</th>
                    <th className="px-6 py-4 text-sm font-semibold text-primary-900 uppercase tracking-wider">Passcode</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {zoomLinks.map((entry, i) => (
                    <tr key={i} className="hover:bg-primary-50/50 transition-colors duration-150">
                      <td className="px-6 py-4 text-sm font-medium text-primary-900">
                        {entry.grade}
                        <span className="block text-xs text-secondary-600 font-normal mt-0.5">{entry.note}</span>
                      </td>
                      <td className="px-6 py-4 text-sm">{renderZoomLink(entry.link)}</td>
                      <td className="px-6 py-4 text-sm text-secondary-700 font-mono">{entry.id}</td>
                      <td className="px-6 py-4 text-sm text-secondary-700 font-mono">{entry.passcode}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="mt-6 text-sm text-secondary-600 text-center">
              Questions? Contact us at{' '}
              <a href="tel:+12128606003" className="text-primary-600 hover:text-primary-800 underline underline-offset-2 transition-colors">
                (212) 860-6003
              </a>
              . — The TAG Administrative Team
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CurriculumConference;
